import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";

import { Button } from "V2.0/common/button";
import { Field, Label, ErrorMessage } from "V2.0/common/fieldset";
import { Input } from "V2.0/common/input";

import { SectionCard } from "./components/SectionCard";
import { WhenItRuns } from "./sections/WhenItRuns";
import { HowWeDecide } from "./sections/HowWeDecide";
import { TheRequest } from "./sections/TheRequest";
import { WhatTheCallerHears, LanguageSettings } from "./sections/WhatTheCallerHears";
import { SaveFromResponse } from "./sections/SaveFromResponse";
import { Advanced } from "./sections/Advanced";

import { EditorState, editorStateFromItem, payloadFromEditorState, validateEditorState } from "./models/editorState";
import { sectionsFor, decideCopy } from "./models/sections";
import { initialFocus, stateFor, advance, reveal, clampFocus } from "./models/guidedFocus";

type Props = {
  item: any;
  goBack: () => void;
  onConfirm: (payload: any) => void;
  contextCharacteristics?: any[];
  allAPIs?: any[];
  languageSettings?: LanguageSettings | null;
};

export function IndividualAPI({ item, goBack, onConfirm, contextCharacteristics = [], allAPIs = [], languageSettings }: Props) {
  const isNew = !item?._id;
  const [state, setState] = useState<EditorState>(() => editorStateFromItem(item));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focus, setFocus] = useState(() => initialFocus(isNew ? "add" : "edit"));

  const sections = sectionsFor(state.triggerEvent);
  const numbered = sections.filter((section) => section.number !== null);
  const advancedSection = sections.find((section) => section.id === "advanced");
  const decide = decideCopy(state.triggerEvent);
  const defaultLanguage = (languageSettings?.defaultLanguage ?? "").trim().toLowerCase() || "en";

  useEffect(() => {
    setFocus((current) => clampFocus(current, numbered.length));
  }, [numbered.length]);

  const variableSuggestions = useMemo(() => {
    const names = new Set<string>();
    for (const api of allAPIs ?? []) {
      for (const param of api?.returnParams ?? []) {
        if (param?.persistParam && param?.useLocation === "API" && param?.paramName) names.add(param.paramName);
      }
    }
    for (const field of state.responseFields) if (field.saveTo === "laterRequests" && field.name) names.add(field.name);
    return Array.from(names).sort();
  }, [allAPIs, state.responseFields]);

  const patch = (changes: Partial<EditorState>) => {
    setState((current) => ({ ...current, ...changes }));
    const touched = Object.keys(changes);
    if (touched.length > 0) {
      setErrors((current) => {
        const next = { ...current };
        for (const key of touched) {
          delete next[key];
          if (key === "messages") for (const errorKey of Object.keys(next)) if (errorKey.startsWith("messages.")) delete next[errorKey];
        }
        return next;
      });
    }
  };

  const handleSave = () => {
    const validation = validateEditorState(state);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      // Open everything so the customer can see where the problem is
      setFocus((current) => reveal(current, Math.max(numbered.length - 1, 0)));
      return;
    }
    onConfirm(payloadFromEditorState(state, defaultLanguage));
    goBack();
  };

  const renderSection = (id: string) => {
    switch (id) {
      case "when":
        return (
          <WhenItRuns
            triggerEvent={state.triggerEvent}
            callDirection={state.callDirection}
            callOnce={state.callOnce}
            callStatusMatchValues={state.callStatusMatchValues}
            errors={errors}
            onChange={patch}
          />
        );
      case "decide":
        return decide ? (
          <HowWeDecide copy={decide} apiGatePrompt={state.apiGatePrompt} apiTriggerPrompt={state.apiTriggerPrompt} errors={errors} onChange={patch} />
        ) : null;
      case "request":
        return (
          <TheRequest
            triggerEvent={state.triggerEvent}
            apiMethod={state.apiMethod}
            apiURL={state.apiURL}
            requestFields={state.requestFields}
            conversationLabel={decide?.conversationLabel ?? "Pulled out of the conversation"}
            variableSuggestions={variableSuggestions}
            errors={errors}
            onChange={patch}
            onFieldsChange={(requestFields) => patch({ requestFields })}
          />
        );
      case "hears":
        return (
          <WhatTheCallerHears messages={state.messages} languageSettings={languageSettings} errors={errors} onChange={(messages) => patch({ messages })} />
        );
      case "save":
        return <SaveFromResponse responseFields={state.responseFields} onFieldsChange={(responseFields) => patch({ responseFields })} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Button className="self-start" plain onClick={goBack}>
        <ArrowLeftIcon />
        <span className="text-blue-600">Back to all APIs</span>
      </Button>

      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-zinc-900">{isNew ? "New custom API" : "Edit custom API"}</h2>
        <Field>
          <Label>Name</Label>
          <Input
            name="apiName"
            value={state.apiName}
            onChange={(event) => patch({ apiName: event.target.value })}
            invalid={!!errors.apiName}
            placeholder="e.g. Place order"
          />
          {errors.apiName && <ErrorMessage>{errors.apiName}</ErrorMessage>}
        </Field>
      </div>

      {numbered.map((section, index) => (
        <SectionCard
          key={section.id}
          id={section.id}
          number={section.number}
          title={section.title}
          summary={section.summary}
          state={stateFor(focus, index, numbered.length)}
          nextLabel={index < numbered.length - 1 ? `Next: ${numbered[index + 1].title}` : null}
          onNext={() => setFocus((current) => advance(current, numbered.length))}
          onReveal={() => setFocus((current) => reveal(current, index))}
        >
          {renderSection(section.id)}
        </SectionCard>
      ))}

      {advancedSection && (
        <SectionCard id="advanced" number={null} title={advancedSection.title} summary={advancedSection.summary} state="open">
          <Advanced
            triggerEvent={state.triggerEvent}
            currentId={item?._id}
            allAPIs={allAPIs}
            contextCharacteristics={contextCharacteristics}
            contextFilter={state.contextFilter}
            sequenceAPIIds={state.sequenceAPIIds}
            activationAPIIds={state.activationAPIIds}
            onChange={patch}
          />
        </SectionCard>
      )}

      <div className="flex justify-end gap-x-3 py-4">
        <Button outline onClick={goBack}>
          Cancel
        </Button>
        <Button onClick={handleSave}>{isNew ? "Save API" : "Save changes"}</Button>
      </div>
    </div>
  );
}
